import { createImage } from "@gluestack-ui/core/image/creator";
import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import {
  Image as ExpoImage,
  ImageErrorEventData,
  ImageLoadEventData,
  ImageStyle,
} from "expo-image";
import { cssInterop } from "nativewind";
import React, { useCallback, useState } from "react";
import {
  LayoutChangeEvent,
  PixelRatio,
  Platform,
  Image as RNImage,
} from "react-native";
import { Box } from "../box";
import { ImagePlaceholder } from "./placeholder";
import { snapToWidthLadder } from "./responsiveSource";
import { imageStyle } from "./styles";
import {
  asImageRef,
  type BoxSize,
  isBundledAsset,
  normalizeSource,
  resizeSource,
  resolveLoadStatus,
  type SettledLoad,
  sourceKeyOf,
  withStableCacheKey,
} from "./utils";

cssInterop(ExpoImage, { className: "style" });

const UIImage = createImage({ Root: ExpoImage });
cssInterop(UIImage, { className: "style" });

export type ImageProps = Omit<VariantProps<typeof imageStyle>, "status"> &
  React.ComponentProps<typeof UIImage>;

const WEB_SIZE_RESET = (Platform.OS === "web"
  ? { height: "revert-layer", width: "revert-layer" }
  : undefined) as unknown as ImageStyle;

// The physical pixel width to request from the image CDN: the rendered width
// times the pixel ratio, rounded up to the ladder. `null` until measured: the
// `size` variant can't seed it, since `className` often overrides its width.
// The raw layout size is kept too; the placeholder picks its drawing from it.
const useRequestedWidth = (onLayout: ImageProps["onLayout"]) => {
  const [width, setWidth] = useState<number | null>(null);
  const [layoutSize, setLayoutSize] = useState<BoxSize | null>(null);
  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onLayout?.(event);
      const { width: measuredWidth, height } = event.nativeEvent.layout;
      if (measuredWidth <= 0) return;
      // `onLayout` also fires when the box only moves; keep the same object then.
      setLayoutSize((current) =>
        current && current.width === measuredWidth && current.height === height
          ? current
          : { width: measuredWidth, height },
      );
      const step = snapToWidthLadder(measuredWidth * PixelRatio.get());
      // Only ever step up: a smaller box keeps the larger image it already has
      // instead of downloading another one.
      setWidth((current) =>
        current !== null && current >= step ? current : step,
      );
    },
    [onLayout],
  );
  return { requestedWidth: width, layoutSize, handleLayout };
};

const useLoadStatus = (
  sourceKey: string | null,
  onLoad: ImageProps["onLoad"],
  onError: ImageProps["onError"],
) => {
  const [settled, setSettled] = useState<SettledLoad | null>(null);
  // Every step up to a wider image fires `onLoad` again; keep the same state
  // object then, so it doesn't re-render.
  const settle = useCallback(
    (next: SettledLoad) =>
      setSettled((current) =>
        current &&
        current.sourceKey === next.sourceKey &&
        current.status === next.status
          ? current
          : next,
      ),
    [],
  );
  const handleLoad = useCallback(
    (event: ImageLoadEventData) => {
      onLoad?.(event);
      if (sourceKey !== null) settle({ sourceKey, status: "loaded" });
    },
    [onLoad, settle, sourceKey],
  );
  const handleError = useCallback(
    (event: ImageErrorEventData) => {
      onError?.(event);
      if (sourceKey !== null) settle({ sourceKey, status: "failed" });
    },
    [onError, settle, sourceKey],
  );
  return {
    status: resolveLoadStatus(sourceKey, settled),
    handleLoad,
    handleError,
  };
};

const Image = React.forwardRef<
  React.ComponentRef<typeof UIImage>,
  ImageProps & { className?: string; mantainAspect?: boolean }
>(
  (
    {
      size = "md",
      className,
      mantainAspect,
      source,
      placeholder,
      onLayout,
      onLoad,
      onError,
      ...props
    },
    ref,
  ) => {
    const { requestedWidth, layoutSize, handleLayout } =
      useRequestedWidth(onLayout);
    const normalizedSource = normalizeSource(source);
    const { handleLoad, handleError, status } = useLoadStatus(
      sourceKeyOf(normalizedSource),
      onLoad,
      onError,
    );
    // A native ref has no uri to normalize or resize, and gluestack's `UIImage`
    // would rewrite it as `{ uri: ref }` (expo-image then throws while
    // rendering), so it goes straight to expo-image.
    const imageRef = asImageRef(source);
    if (imageRef) {
      return (
        <ExpoImage
          className={imageStyle({ size, class: className })}
          role="img"
          {...props}
          source={imageRef}
          placeholder={placeholder}
          onLayout={onLayout}
          onLoad={onLoad}
          onError={onError}
          // Third-party typing gap: gluestack types its forwarded ref as the
          // props object, but at runtime `UIImage` forwards it to this same
          // `ExpoImage`, so the caller gets the instance it gets on the other path.
          ref={ref as unknown as React.Ref<ExpoImage>}
        />
      );
    }
    // A bundled asset ships with the app and sizes itself (`mantainAspect`,
    // `aspect-auto`), so it renders bare, with no placeholder around it.
    if (isBundledAsset(normalizedSource)) {
      const asset =
        typeof source === "number" ? RNImage.resolveAssetSource(source) : null;
      return (
        <UIImage
          className={imageStyle({ size, class: className })}
          {...props}
          source={normalizedSource}
          placeholder={placeholder}
          onLayout={onLayout}
          onLoad={onLoad}
          onError={onError}
          ref={ref}
          style={[
            WEB_SIZE_RESET,
            mantainAspect && asset?.width && asset.height
              ? { aspectRatio: asset.width / asset.height }
              : undefined,
          ]}
        />
      );
    }
    // A remote image sits over the placeholder, which shows while the source is
    // missing, unmeasured, loading (pulsing) or broken, and goes once it loads.
    const resizedSource = withStableCacheKey(
      resizeSource(normalizedSource, requestedWidth),
    );
    return (
      <Box
        className={imageStyle({ size, status, class: className })}
        onLayout={handleLayout}
      >
        {status !== "loaded" && (
          // Keyed by status: NativeWind remounts a view that gains an
          // `animate-*` class after its first render, so each status mounts
          // its own placeholder instead.
          <ImagePlaceholder key={status} status={status} size={layoutSize} />
        )}
        {resizedSource && (
          <UIImage
            className="absolute inset-0 h-full w-full"
            {...props}
            source={resizedSource}
            placeholder={placeholder}
            onLoad={handleLoad}
            onError={handleError}
            ref={ref}
            style={WEB_SIZE_RESET}
          />
        )}
      </Box>
    );
  },
);

Image.displayName = "Image";
export { Image };
